import {
	type TickerWeightEntry,
	type VirtualInvestmentResponse,
	VirtualInvestmentSourceType,
} from "@eu.mdotm.api/portfolio-lab";
import type { ReviewTicker, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import BigNumber from "bignumber.js";
import { Map as ImmutableMap } from "immutable";
import { DEFAULT_LAB_AUM } from "../../common";
import type {
	PortfolioLabComposition,
	PortfolioLabCompositionInstrument,
	PortfolioLabCompositionPortfolio,
	PortfolioLabInvestmentItem,
	PortfolioLabInvestmentType,
} from "../context";
import { labSum } from "../../utils/math";
import type { PortfolioLabAdapter } from "./contract";

const sourceTypeToLabType: Partial<Record<string, PortfolioLabInvestmentType>> = {
	[VirtualInvestmentSourceType.Portfolio]: "INVESTMENT",
	[VirtualInvestmentSourceType.InvestmentProposal]: "INVESTMENT_PROPOSAL",
	[VirtualInvestmentSourceType.Benchmark]: "BENCHMARK",
	[VirtualInvestmentSourceType.Reference]: "REFERENCE",
};

/** Maps virtual investments (and their flattened compositions) between the API and context state. */
export const virtualInvestmentAdapter = {
	toContext(responses: VirtualInvestmentResponse[]): PortfolioLabInvestmentItem[] {
		return responses
			.filter((r) => r.source?.uuid != null)
			.map((virtualInvestment) => {
				const type = sourceTypeToLabType[virtualInvestment.source!.type] ?? "INVESTMENT";
				const aum = virtualInvestment.totalValue ?? DEFAULT_LAB_AUM;
				const { composition, tickerInfoPool } = compositionFromResponse(virtualInvestment, aum);
				return {
					uuid: virtualInvestment.source!.uuid!,
					virtualUuid: virtualInvestment.uuid,
					name: virtualInvestment.name ?? "",
					type,
					status: type === "INVESTMENT_PROPOSAL" ? ("PROPOSAL_READY" as const) : ("READY" as const),
					currency: virtualInvestment.currency ?? null,
					aum,
					initialAum: aum,
					initialComposition: initialCompositionFromResponse(virtualInvestment, aum),
					currentComposition: composition,
					baselineUnsaved: false,
					compositionRevision: virtualInvestment.compositionRevision,
					reportsComputedAtRevision: virtualInvestment.reportsComputedAtRevision ?? null,
					pendingWrites: 0,
					compositionSaveStatus: "idle",
					commitSettleStatus: "idle",
					pendingCompute: null,
					scenarioFallback: null,
					reportsScenarioId: virtualInvestment.scenarioId ?? null,
					tickerInfoPool,
				} satisfies PortfolioLabInvestmentItem;
			});
	},
	/** `initialWeight` is not sent: the backend owns the baseline and ignores it on PUT. */
	toUpstream(investmentCtx: PortfolioLabInvestmentItem) {
		return {
			composition: investmentCtx.currentComposition.flatMap((c): TickerWeightEntry[] => {
				if (c.type === "instrument") {
					return [{ tickerName: c.tickerName, weight: c.weight, monetaryValue: c.monetaryWeight }];
				}

				return c.instruments.map((i) => ({
					tickerName: i.tickerName,
					weight: i.weight,
					monetaryValue: i.monetaryWeight,
					subPortfolioUuid: c.uuid,
				}));
			}),
			aum: investmentCtx.aum,
			name: investmentCtx.name,
			uuid: investmentCtx.uuid,
			virtualUuid: investmentCtx.virtualUuid,
		};
	},
} satisfies PortfolioLabAdapter;

/**
 * The lab's scenario, read back from the columns a session restored — the seed for
 * `selectedScenarioId$`.
 *
 * One picker drives the whole view and every write sends its value to every column, so the stored
 * ids normally agree. A client that writes them individually (the Sphere MCP's `add_column`) can
 * leave them mixed, which one picker cannot represent: mixed reads as Current Market, and each
 * column keeps its own id until the user picks one — which then writes them all.
 */
export function scenarioIdFromColumns(responses: VirtualInvestmentResponse[]): string | null {
	const ids = responses.map((response) => response.scenarioId ?? null);
	const [first] = ids;
	return first != null && ids.every((id) => id === first) ? first : null;
}

/**
 * Maps a lab investment's current composition to the `ReviewTicker[]` payload accepted by the entity
 * editor save endpoints (`instrumentsEditorRepository.edit`). Mirrors `covertInstrumentEditorToReviewTicker`:
 * one source entry → one ticker. A nested portfolio is emitted as a single `PORTFOLIO_MIXED` reference
 * (`ticker` = the portfolio's own uuid); its underlying instruments are never expanded (spec FR-008).
 */
export function labCompositionToReviewTickers(item: PortfolioLabInvestmentItem): ReviewTicker[] {
	return item.currentComposition.map((c): ReviewTicker => {
		if (c.type === "portfolio") {
			return { ticker: c.uuid, weight: c.weight, proxyOverwriteType: "PORTFOLIO_MIXED" };
		}
		return { ticker: c.tickerName, weight: c.weight };
	});
}

function compositionFromResponse(
	response: VirtualInvestmentResponse,
	aum: number,
): { composition: PortfolioLabComposition[]; tickerInfoPool: ImmutableMap<string, UserInstrumentDto> } {
	const composition = response.composition ?? [];
	const portfolioNames = new Map((response.subPortfolios ?? []).map((sp) => [sp.uuid, sp.name]));

	const tickerInfoPool = ImmutableMap<string, UserInstrumentDto & { subPortfolioUuid?: string }>(
		composition.map((item) => [
			item.tickerName,
			{
				name: item.name,
				tickerName: item.tickerName,
				identifiers: { ISIN: item.isin },
				tags: item.tags,
				subPortfolioUuid: item.subPortfolioUuid,
			},
		]),
	);

	const investmentsLabMap = new Map<string, PortfolioLabCompositionPortfolio>();
	const instrumentsLab: PortfolioLabCompositionInstrument[] = [];

	for (const instrument of composition) {
		if (!instrument.subPortfolioUuid) {
			instrumentsLab.push({
				type: "instrument",
				tickerName: instrument.tickerName,
				monetaryWeight: instrument.monetaryValue,
				weight: instrument.weight,
			});
			continue;
		}

		if (!investmentsLabMap.has(instrument.subPortfolioUuid)) {
			investmentsLabMap.set(instrument.subPortfolioUuid, {
				type: "portfolio",
				uuid: instrument.subPortfolioUuid,
				instruments: [],
				monetaryWeight: 0,
				name: portfolioNames.get(instrument.subPortfolioUuid) ?? "Missing investment name",
				weight: 0,
			});
		}

		const investmentLab = investmentsLabMap.get(instrument.subPortfolioUuid);
		if (investmentLab) {
			investmentLab.instruments.push({
				tickerName: instrument.tickerName,
				monetaryWeight: instrument.monetaryValue,
				weight: instrument.weight,
			});
		}
	}

	// A nested portfolio weighs what its instruments weigh, the definition of `subPortfolios[].weight`,
	// which the server does not keep in step with them (MDOT-25921).
	const portfoliosLab = [...investmentsLabMap.values()].map((portfolio): PortfolioLabCompositionPortfolio => {
		const weight = labSum(portfolio.instruments, (i) => i.weight);
		return { ...portfolio, weight, monetaryWeight: monetaryFromWeight(weight, aum) };
	});

	return { composition: [...portfoliosLab, ...instrumentsLab], tickerInfoPool };
}

/**
 * The column's baseline — the composition its weight deltas are measured against — rebuilt from each
 * holding's `initialWeight`, which the backend captures when the column is created and never moves on
 * autosave. So a delta survives a reload, and shows for an edit made while the lab page was closed.
 *
 * A holding whose `initialWeight` is 0 was added after the baseline, so it is left out: its delta is its
 * whole weight, and a Reset drops it, as the server's reset does. A nested portfolio's weight is the sum
 * of its instruments' baselines.
 *
 * The backend keys `initialWeight` by ticker name alone, so a ticker held in more than one place carries
 * one of its baselines on every row — TODO(MDOT-25254).
 */
function initialCompositionFromResponse(response: VirtualInvestmentResponse, aum: number): PortfolioLabComposition[] {
	const portfolioNames = new Map((response.subPortfolios ?? []).map((sp) => [sp.uuid, sp.name]));
	const nested = new Map<string, PortfolioLabCompositionPortfolio["instruments"]>();
	const direct: PortfolioLabCompositionInstrument[] = [];

	for (const entry of response.composition ?? []) {
		const weight = entry.initialWeight ?? entry.weight;
		if (weight === 0) {
			continue;
		}
		const holding = { tickerName: entry.tickerName, weight, monetaryWeight: monetaryFromWeight(weight, aum) };
		if (!entry.subPortfolioUuid) {
			direct.push({ type: "instrument", ...holding });
			continue;
		}
		const instruments = nested.get(entry.subPortfolioUuid) ?? [];
		instruments.push(holding);
		nested.set(entry.subPortfolioUuid, instruments);
	}

	const portfolios = [...nested].map(([uuid, instruments]): PortfolioLabCompositionPortfolio => {
		const weight = labSum(instruments, (i) => i.weight);
		return {
			type: "portfolio",
			uuid,
			name: portfolioNames.get(uuid) ?? "Missing investment name",
			weight,
			monetaryWeight: monetaryFromWeight(weight, aum),
			instruments,
		};
	});

	return [...portfolios, ...direct];
}

function monetaryFromWeight(weight: number, aum: number): number {
	return BigNumber(weight).div(100).times(aum).dp(2, BigNumber.ROUND_HALF_UP).toNumber();
}
